import { useTranslation } from "react-i18next"
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react"
import { isUndefined } from "lodash"
import { BaseCheckboxPropsOverrides, GridColDef } from "@mui/x-data-grid"
import { MenuItem, SelectChangeEvent } from "@mui/material"

import { colors } from "@/components/colors"
import { colorTokens } from "@/components/colorTokens"
import Icon from "@/components/icon/Icon"
import IconButton from "@/components/button/IconButton"
import Selector from "@/components/form/selector/Selector"
import BasicButton from "@/components/button/BasicButton"
import TextField from "@/components/form/textField/TextField"
import Chip from "@/components/chip/Chip"
import Table from "@/components/table/Table"
import Pagination from "@/components/table/Pagination"
import { BaseCheckbox } from "@/components/form/checkbox/Checkbox"
import Radio from "@/components/form/radio/Radio"
import Dialog from "@/components/dialog/Dialog"
import { convertGender, convertGroupAge } from "@/utils/format"
import { useOtherPlayersQuery } from "@/hooks/api/useSettingQuery"
import { useUpsertMovePlayersMutation } from "@/hooks/api/usePlayerQuery"
import { createGlobalStore } from "@/stores/global"
import { createAccountStore } from "@/stores/account"
import { Team } from "@/types/team"
import { PlayerSetting } from "@/types/setting"
import { CheckboxProps } from "@/types/components/checkbox"

import "./playerTransfer.scss"

export default function PlayerTransferPage() {
  const { t } = useTranslation()
  const { teamInfo: teamList, teamProfileInfo } = createAccountStore()
  const setSnackBar = createGlobalStore((state) => state.setSnackBar)

  const [beforeTeam, setBeforeTeam] = useState<Team | undefined>()
  const [afterTeam, setAfterTeam] = useState<Team | undefined>()
  const [beforePlayer, setBeforePlayer] = useState<PlayerSetting[]>([])
  const [afterPlayer, setAfterPlayer] = useState<PlayerSetting[]>([])
  const [tablePlayer, setTablePlayer] = useState<PlayerSetting[]>([])
  const [searchValue, setSearchValue] = useState<string>("")
  const [modalOpen, setModalOpen] = useState(false)
  const [tempTeamId, setTempTeamId] = useState<number | null>(null) // 선택된 선수가 있는 경우, 도착 팀을 변경하는 경우 임시로 변경할 팀 id 를 저장하는 state
  const [tempMoveBtnDisabled, setTempMoveBtnDisabled] = useState(false)

  const playerRef = useRef<PlayerSetting[] | null>(null)

  const { data: player, isLoading } = useOtherPlayersQuery(beforeTeam?.idfTeam)
  const { mutateAsync, isPending } = useUpsertMovePlayersMutation()

  useEffect(() => {
    const selectedTeam = teamList.find((team) => team.idfTeam === teamProfileInfo?.idfTeam)
    setBeforeTeam(selectedTeam)

    if (selectedTeam === teamList[0]) {
      const filteredTeamList = teamList.filter((team) => team.idfTeam !== teamProfileInfo?.idfTeam)
      setAfterTeam(filteredTeamList[0])
    } else {
      setAfterTeam(teamList[0])
    }
  }, [teamProfileInfo, teamList])

  useEffect(() => {
    if (player) {
      setTablePlayer(player)
      playerRef.current = player
    }
  }, [player])

  useEffect(() => {
    if (searchValue.length === 1) {
      return
    }
    if (searchValue.length === 0) {
      setTablePlayer(
        (playerRef.current || []).filter(
          (player) => !afterPlayer.some((after) => after.idfPlayer === player.idfPlayer),
        ),
      )

      return
    }

    const filtered = (playerRef.current || [])
      .filter((player) => !afterPlayer.some((after) => after.idfPlayer === player.idfPlayer))
      .filter((player) => player.nickname.toLowerCase().includes(searchValue.toLowerCase()))

    setTablePlayer(filtered)
  }, [searchValue, playerRef, afterPlayer])

  useEffect(() => {
    // 변화가 생기면 move 버튼을 활성화
    if (beforePlayer.length) {
      setTempMoveBtnDisabled(false)
    }
  }, [beforePlayer])

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "uniformNumber",
        renderHeader: () => t("jersey"),
        width: 100,
      },
      {
        field: "nickname",
        renderHeader: () => t("playerName"),
        maxWidth: 330,
        flex: 1,
      },
      {
        field: "position",
        renderHeader: () => t("position"),
        maxWidth: 96,
        flex: 1,
      },
    ],
    [t],
  )

  const isDisabledMoveBtn = useMemo(() => {
    const emptyTeam = !beforeTeam || !afterTeam
    const sameTeam = beforeTeam?.idfTeam === afterTeam?.idfTeam
    const emptyPlayer = !beforePlayer.length && !afterPlayer.length

    return emptyTeam || sameTeam || emptyPlayer || tempMoveBtnDisabled
  }, [beforeTeam, afterTeam, beforePlayer, afterPlayer, tempMoveBtnDisabled])

  const onClickDeleteAfter = (idfPlayer: number) => {
    if (playerRef.current) {
      setAfterPlayer((prev) => prev.filter((p) => p.idfPlayer !== idfPlayer))

      setTablePlayer((prev) => {
        const restored = playerRef.current
          ? [...prev, playerRef.current?.find((p) => p.idfPlayer === idfPlayer)]
          : [...prev]

        return playerRef.current
          ? playerRef.current.filter((p) => restored.some((r) => r && r.idfPlayer === p.idfPlayer))
          : prev
      })
    }
  }

  const onClickMove = () => {
    setAfterPlayer((prev) => [...prev, ...beforePlayer])
    setTablePlayer((prev) =>
      prev.filter((player) => !beforePlayer.some((p) => p.idfPlayer === player.idfPlayer)),
    )
    setBeforePlayer([])
    setTempMoveBtnDisabled(true)
  }

  const onClickSave = async () => {
    try {
      if (!beforeTeam || !afterTeam) {
        return
      }
      const beforeTeamId = beforeTeam.idfTeam
      const afterTeamId = afterTeam.idfTeam
      const playerIds = afterPlayer.map((player) => player.idfPlayer)
      await mutateAsync({
        srcIdfTeam: beforeTeamId,
        destIdfTeam: afterTeamId,
        players: playerIds,
      })
      setSnackBar({
        open: true,
        severity: "success",
        message: t("messages.successUpdate"),
      })
      setAfterPlayer([])
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <>
      <div className="PlayerTransferCont">
        <div className="PlayerTransferBeforeCont">
          <SwitchTeamSelector
            teamList={teamList}
            teamValue={beforeTeam}
            oppositeValue={afterTeam}
            onChangeTeam={(e: SelectChangeEvent<number | string>) => {
              const selectedTeam = teamList.find((team) => team.idfTeam === e.target.value)
              setBeforeTeam(selectedTeam)
            }}
          />
          <div className="PlayerTransferSelectCont">
            <div className="PlayerSearchCont">
              <span>
                <p>{t("player")}</p>
                <Chip type="number" label={beforePlayer.length + ""} />
              </span>
              <TextField
                type="search"
                placeholder={t("search")}
                appendIcon={<Icon type="search" />}
                value={searchValue || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearchValue(e.target.value)
                }}
              />
            </div>
            <Table
              columns={columns}
              rows={tablePlayer}
              slots={{
                pagination: Pagination,
              }}
              getRowId={(row) => row.idfPlayer || ""}
              selectionType="checkbox"
              rowSelectionModel={{
                type: "include",
                ids: new Set(beforePlayer.map((player) => player.idfPlayer)),
              }}
              onRowSelectionModelChange={(newSelection) => {
                if (!tablePlayer || !newSelection.ids) {
                  return
                }
                setBeforePlayer(tablePlayer.filter((row) => newSelection.ids.has(row.idfPlayer)))
              }}
              selectionElement={(props) => {
                type NewPropsOverrides = CheckboxProps &
                  BaseCheckboxPropsOverrides & { slotProps: { [key: string]: any } }
                const isHeader = (props as NewPropsOverrides).slotProps?.htmlInput?.[
                  "aria-label"
                ]?.includes("all")
                const onChangeTableCheckbox = (
                  e: ChangeEvent<HTMLInputElement>,
                  checked: boolean,
                ) => {
                  if (isHeader) {
                    if (props.checked && !props.indeterminate) {
                      return setBeforePlayer([])
                    }
                    if (tablePlayer) {
                      return setBeforePlayer(tablePlayer)
                    }
                  } else {
                    return props.onChange?.(e, checked)
                  }
                }

                return (
                  <BaseCheckbox
                    checked={props.checked}
                    indeterminate={props.indeterminate}
                    onChange={onChangeTableCheckbox}
                    disabled={props.disabled}
                  />
                )
              }}
              disableColumnResize
              disableColumnSorting
              loading={isLoading}
              sx={{
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: `${colorTokens.background["alpha-grey-2"]} !important`,
                },
                "& .MuiDataGrid-columnHeader": {
                  cursor: "default",
                },
              }}
            />
          </div>
          <BasicButton
            type="primary-contained"
            size="md"
            className="PlayerTransferMoveBtn"
            onClick={onClickMove}
            disabled={isDisabledMoveBtn}>
            <span>
              {t("button.transfer")}
              <Icon type="arrow-right" color={colors.neutral?.grey[300]} size={16} />
            </span>
          </BasicButton>
        </div>
        <Icon type="arrow-right-double" color={colors.neutral?.grey[400]} size={24} />
        <div className="PlayerTransferAfterCont">
          <SwitchTeamSelector
            teamList={teamList}
            teamValue={afterTeam}
            oppositeValue={beforeTeam}
            onChangeTeam={(e: SelectChangeEvent<number | string>) => {
              if (afterPlayer.length) {
                setModalOpen(true)
                setTempTeamId(e.target.value as number)
              } else {
                const selectedTeam = teamList.find((team) => team.idfTeam === e.target.value)
                setAfterTeam(selectedTeam)
              }
            }}
          />

          <div className="PlayerTransferSelectCont">
            <div className="PlayerSearchCont">
              <span>
                <p>{t("player")}</p>
                <Chip type="number" label={afterPlayer.length + ""} />
              </span>
            </div>
            <Table
              columns={[
                ...columns,
                {
                  field: "idfPlayer",
                  headerName: "",
                  width: 32,
                  renderCell: ({ row }) => (
                    <span className="after-team-delete-player">
                      <IconButton
                        type="ghost"
                        icon="close"
                        color={colors.neutral.grey[500]}
                        size="xs"
                        onClick={() => onClickDeleteAfter(row.idfPlayer || 0)}
                      />
                    </span>
                  ),
                },
              ]}
              rows={afterPlayer || []}
              getRowId={(row) => `${row.idfPlayer || 0}-after-team`}
              slots={{
                pagination: Pagination,
              }}
              disableColumnResize
              disableColumnSorting
              columnHeaderHeight={44}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  cursor: "default",
                },
              }}
            />
          </div>
        </div>
      </div>

      <BasicButton
        type="primary-contained"
        size="lg"
        className="PlayerTransferSaveBtn"
        onClick={onClickSave}
        disabled={!afterPlayer.length || isPending}>
        {t("button.save")}
      </BasicButton>

      <Dialog
        open={modalOpen}
        title={t("confirm")}
        onClose={() => setModalOpen(false)}
        onConfirm={() => {
          if (!tempTeamId) {
            return
          }
          setAfterPlayer([])
          setAfterTeam(teamList.find((team) => team.idfTeam === tempTeamId))
          setBeforePlayer(playerRef.current || [])
          setModalOpen(false)
        }}
        confirmTextKey="change"
        maxWidth="400px">
        <p>{t("messages.clearChangeTeam")}</p>
      </Dialog>
    </>
  )
}

function SwitchTeamSelector({
  teamList,
  teamValue,
  oppositeValue,
  onChangeTeam,
}: {
  teamList: Team[]
  teamValue: Team | undefined
  oppositeValue: Team | undefined
  onChangeTeam: (e: SelectChangeEvent<number | string>) => void
}) {
  return (
    <Selector
      type="select"
      value={teamValue?.idfTeam || ""}
      onChange={onChangeTeam}
      renderValue={(value) => {
        const selectedTeam = teamList.find((team) => team.idfTeam === value)

        if (isUndefined(value) || !selectedTeam) {
          return null
        }

        return (
          <p className="team-switch-team-label selected-team-label">
            {selectedTeam.name}
            <span>
              {convertGroupAge(selectedTeam.groupAge)}/{convertGender(selectedTeam.gender)}
            </span>
          </p>
        )
      }}
      MenuProps={{
        disablePortal: true,
        PaperProps: {
          sx: {
            maxWidth: "560px",
            maxHeight: "280px",
          },
        },
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: "top",
          horizontal: "left",
        },
      }}>
      {teamList.map((team) => (
        <MenuItem
          key={team.idfTeam}
          value={team.idfTeam}
          disabled={team.idfTeam === oppositeValue?.idfTeam}>
          <Radio
            value={team.idfTeam || null}
            label=""
            checked={team.idfTeam === teamValue?.idfTeam}
            disabled={team.idfTeam === oppositeValue?.idfTeam}
          />
          <p className="team-switch-team-label menu-item-label">
            {team.name}
            <span>
              {convertGroupAge(team.groupAge)}/{convertGender(team.gender)}
            </span>
          </p>
        </MenuItem>
      ))}
    </Selector>
  )
}
